import {useEffect, useRef, useState} from 'react';
import Cropper from 'react-easy-crop';
import type {Area, MediaSize, Point, Size} from 'react-easy-crop';
import {useTranslation} from 'react-i18next';
import {Minus, Plus} from 'lucide-react';
import {IconButton, Slider} from '@chekinapp/ui';
import {clampCropPosition, getCropArea, getFitZoom} from '../utils/cropBounds';

const CROP_ASPECT = 410 / 262;
const FRAME_WIDTH_RATIO = 0.9;
const FRAME_HEIGHT_RATIO = 0.86;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

function getCropSize(width: number, height: number): Size {
  const frameWidth = Math.min(
    width * FRAME_WIDTH_RATIO,
    height * FRAME_HEIGHT_RATIO * CROP_ASPECT,
  );
  return {width: frameWidth, height: frameWidth / CROP_ASPECT};
}

type Props = {
  image: string;
  onCropComplete: (croppedAreaPixels: Area) => void;
};

const BRACKET_OFFSET = 6;

const BRACKET_CORNERS = [
  'left-0 top-0 rounded-tl-[14px] border-l-[3px] border-t-[3px]',
  'right-0 top-0 rounded-tr-[14px] border-r-[3px] border-t-[3px]',
  'bottom-0 left-0 rounded-bl-[14px] border-b-[3px] border-l-[3px]',
  'bottom-0 right-0 rounded-br-[14px] border-b-[3px] border-r-[3px]',
];

const ImageCropper = ({image, onCropComplete}: Props) => {
  const {t} = useTranslation('identity-verification');
  const stageRef = useRef<HTMLDivElement>(null);
  const [crop, setCrop] = useState<Point>({x: 0, y: 0});
  const [chosenZoom, setChosenZoom] = useState<number | null>(null);
  const [cropSize, setCropSize] = useState<Size | undefined>();
  const [mediaSize, setMediaSize] = useState<MediaSize | undefined>();

  const minZoom = mediaSize && cropSize ? getFitZoom(mediaSize, cropSize) : 1;
  const zoom = Math.max(minZoom, chosenZoom ?? minZoom);
  const boundedCrop =
    mediaSize && cropSize ? clampCropPosition(crop, mediaSize, cropSize, zoom) : crop;
  const changeZoom = (value: number) =>
    setChosenZoom(Math.min(MAX_ZOOM, Math.max(minZoom, value)));

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return;

    const measure = () => {
      if (!stage.clientWidth || !stage.clientHeight) return;
      setCropSize(getCropSize(stage.clientWidth, stage.clientHeight));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!mediaSize || !cropSize) return;
    onCropComplete(
      getCropArea({x: boundedCrop.x, y: boundedCrop.y}, mediaSize, cropSize, zoom),
    );
  }, [boundedCrop.x, boundedCrop.y, mediaSize, cropSize, zoom, onCropComplete]);

  return (
    <>
      <div
        ref={stageRef}
        className="relative h-[min(44svh,380px)] min-h-[240px] w-full lg:aspect-[410/262] lg:h-auto max-w-[var(--capture-max-width,480px)] cursor-grab touch-none overflow-hidden rounded-[18px] bg-[image:var(--capture-view-gradient)] active:cursor-grabbing"
      >
        {cropSize && (
          <>
            <Cropper
              key={`${cropSize.width}x${cropSize.height}`}
              image={image}
              crop={boundedCrop}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={MAX_ZOOM}
              aspect={CROP_ASPECT}
              cropSize={cropSize}
              showGrid={false}
              restrictPosition={false}
              setMediaSize={setMediaSize}
              onCropChange={setCrop}
              onZoomChange={setChosenZoom}
              style={{
                containerStyle: {background: 'transparent'},
                cropAreaStyle: {
                  border: 0,
                  borderRadius: 10,
                  boxShadow: '0 0 0 9999px var(--capture-scrim)',
                },
              }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 [filter:drop-shadow(0_0_6px_var(--capture-bracket))]"
              style={{
                width: cropSize.width + BRACKET_OFFSET * 2,
                height: cropSize.height + BRACKET_OFFSET * 2,
              }}
            >
              {BRACKET_CORNERS.map(corner => (
                <span
                  key={corner}
                  className={`absolute size-9 border-[var(--capture-bracket)] ${corner}`}
                />
              ))}
            </div>
          </>
        )}
        <span className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/60 px-3.5 py-1.5 text-[14px] font-semibold leading-5 text-white">
          {t('drag_zoom_fit_frame')}
        </span>
      </div>
      <div className="mt-3 flex w-full max-w-[var(--capture-max-width,480px)] items-center gap-2.5">
        <IconButton
          label={t('zoom_out')}
          variant="soft"
          shape="circle"
          className="size-10 bg-[var(--chekin-color-white)] text-[var(--chekin-text-primary)] [&_svg]:size-[18px]"
          onClick={() => changeZoom(zoom - ZOOM_STEP)}
          icon={<Minus />}
        />
        <Slider
          className="[--slider-thumb-bg:var(--chekin-color-brand-blue)] [--slider-thumb-border:transparent] [--slider-track-bg:color-mix(in_srgb,var(--chekin-color-brand-navy)_12%,transparent)] [&_[role=slider]]:size-[18px] [&_[role=slider]]:shadow-none [&_[role=slider]]:hover:scale-100 [&_[data-orientation=horizontal].grow]:h-2"
          min={minZoom}
          max={MAX_ZOOM}
          step={0.05}
          value={[zoom]}
          onValueChange={([value]) => changeZoom(value)}
        />
        <IconButton
          label={t('zoom_in')}
          variant="soft"
          shape="circle"
          className="size-10 bg-[var(--chekin-color-white)] text-[var(--chekin-text-primary)] [&_svg]:size-[18px]"
          onClick={() => changeZoom(zoom + ZOOM_STEP)}
          icon={<Plus />}
        />
      </div>
    </>
  );
};

export {ImageCropper};
